<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import DOMPurify from 'dompurify'
import { apiClient } from '@/api/client'
import { useTourStore } from '@/stores/tour'
import { useAuthStore } from '@/stores/auth'

// Popup di transizione mostrato una sola volta per utente al primo accesso.
// Testo configurabile da /admin/guide (sezione con questo slug).
const SLUG = 'transizione-benvenuto'

const { t } = useI18n()
const tourStore = useTourStore()
const auth = useAuthStore()

const visible = ref(false)
const titoloDefault = t('layout.transizione.benvenuto', 'Benvenuto nel nuovo portale')
const titolo = ref(titoloDefault)
const html = ref('')

onMounted(async () => {
  // In passpartout niente popup di benvenuto (lo staff impersona un utente): non gatare nemmeno
  // il tour, che comunque non parte in passpartout (vedi useTour).
  if (auth.me?.passpartout) return
  // Cambio password obbligatorio: nessun popup di benvenuto sopra un dialog non chiudibile.
  // Non si "recupera" alla caduta del flag: è un annuncio una-volta-sola tracciato lato server
  // (giaVisto), quindi comparirà al prossimo accesso, quando la schermata è libera.
  if (auth.me?.mustChangePassword) return
  // Sospende subito l'auto-start del tour dashboard: se il popup si mostra,
  // il tour partira' alla chiusura; altrimenti sblocchiamo (vedi sotto).
  tourStore.gateWelcome()
  try {
    const { data } = await apiClient.get(`/api/v2/onboarding/popup/${SLUG}`)
    if (data && !data.giaVisto && data.contenutoHtml) {
      titolo.value = data.titolo || titoloDefault
      html.value = DOMPurify.sanitize(data.contenutoHtml)
      visible.value = true
    } else {
      tourStore.ungateWelcome()
    }
  } catch {
    // 404 (guida assente/disattivata) o errore di rete: non mostrare nulla
    tourStore.ungateWelcome()
  }
})

async function chiudi() {
  visible.value = false
  try {
    await apiClient.post(`/api/v2/onboarding/popup/${SLUG}/visto`)
  } catch {
    // best-effort: se fallisce, il popup riapparira' al prossimo login
  }
  // Sblocca: se un tour si era prenotato parte da sé; altrimenti si avvia quello della pagina
  // corrente (registrato dalla view), come prima. Mai due volte lo stesso tour.
  if (!tourStore.ungateWelcome()) tourStore.start()
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :closable="false"
    :draggable="false"
    :show-header="false"
    :style="{ width: '44rem', maxWidth: '95vw', overflow: 'hidden' }"
    :breakpoints="{ '640px': '95vw' }"
    :pt="{ content: { class: 'transizione-popup-pt-content' } }"
  >
    <!-- Hero moderno con gradiente + icona AI -->
    <div class="transizione-hero">
      <div class="transizione-hero-icon">
        <i class="pi pi-sparkles" />
      </div>
      <h2 class="transizione-hero-title">
        {{ titolo }}
      </h2>
    </div>

    <!-- contenuto sanitizzato con DOMPurify (testo amministrato via guida) -->
    <div
      class="transizione-popup-content"
      v-html="html"
    />

    <!-- Box social + app (statico): seguici su Facebook + scarica l'app -->
    <div class="transizione-social">
      <a
        href="https://www.facebook.com/immigreersoftware"
        target="_blank"
        rel="noopener noreferrer"
        class="transizione-fb"
      >
        <i class="pi pi-facebook" />
        <span>{{ t('layout.transizione.facebook', 'Seguici su Facebook') }}</span>
      </a>
      <div class="transizione-app">
        <span class="transizione-app-label">{{ t('layout.transizione.scaricaApp', "Scarica la nostra app, se non l'hai già fatto:") }}</span>
        <div class="transizione-stores">
          <a
            href="https://play.google.com/store/apps/details?id=it.immigreer.mobile2"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="t('layout.footer.googlePlay', 'Disponibile su Google Play')"
          >
            <img src="/images/badge-google-play.png" :alt="t('layout.footer.googlePlay', 'Disponibile su Google Play')" class="store-google" >
          </a>
          <a
            href="https://apps.apple.com/us/app/immiapphr/id1611192623?itsct=apps_box_badge&amp;itscg=30200"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="t('layout.footer.appStore', 'Scarica su App Store')"
          >
            <img src="/images/badge-app-store.svg" :alt="t('layout.footer.appStore', 'Scarica su App Store')" class="store-apple" >
          </a>
        </div>
      </div>
    </div>

    <div class="transizione-footer">
      <Button
        :label="t('layout.transizione.iniziaTour', 'Inizia il tour')"
        icon="pi pi-arrow-right"
        icon-pos="right"
        size="large"
        @click="chiudi"
      />
    </div>
  </Dialog>
</template>

<style scoped>
:deep(.transizione-popup-pt-content) {
  padding: 0;
}

/* --- Hero --- */
.transizione-hero {
  background: linear-gradient(135deg, var(--p-primary-500), var(--p-primary-700));
  color: #fff;
  padding: 2rem 2rem 1.75rem;
  text-align: center;
}
.transizione-hero-icon {
  width: 3.5rem;
  height: 3.5rem;
  margin: 0 auto 0.75rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}
.transizione-hero-icon .pi {
  font-size: 1.75rem;
}
.transizione-hero-title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
}

/* --- Corpo --- */
.transizione-popup-content {
  padding: 1.5rem 2rem 0.5rem;
  line-height: 1.55;
}
.transizione-popup-content :deep(.popup-lead) {
  margin-top: 0;
}
.transizione-popup-content :deep(h3) {
  font-size: 1.05rem;
  margin: 1.25rem 0 0.5rem;
}
.transizione-popup-content :deep(ul) {
  padding-left: 1.25rem;
  margin: 0.25rem 0;
}
.transizione-popup-content :deep(li) {
  margin-bottom: 0.35rem;
}

/* Callout assistente AI */
.transizione-popup-content :deep(.popup-ai-callout) {
  margin: 1.25rem 0;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  border-left: 4px solid var(--p-primary-500);
  /* Tint primario adattivo: leggibile in light e dark (era primary-50 fisso → testo invisibile in dark) */
  background: color-mix(in srgb, var(--p-primary-500) 12%, var(--surf-card));
}
.transizione-popup-content :deep(.popup-ai-title) {
  margin: 0 0 0.4rem;
  font-weight: 600;
  color: var(--p-primary-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.transizione-popup-content :deep(.popup-ai-callout p:last-child) {
  margin-bottom: 0;
}

/* Callout Novità WhatsApp — colore verde WhatsApp, distinto dal callout AI */
.transizione-popup-content :deep(.popup-whatsapp-callout) {
  margin: 1.25rem 0;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  border-left: 4px solid #25d366;
  /* Tint verde WhatsApp adattivo: leggibile in light e dark (era #e9f9f0 fisso → testo invisibile in dark) */
  background: color-mix(in srgb, #25d366 14%, var(--surf-card));
}
.transizione-popup-content :deep(.popup-whatsapp-title) {
  margin: 0 0 0.4rem;
  font-weight: 600;
  /* Verde adattivo: si scurisce in light, si schiarisce in dark mantenendo l'identità WhatsApp */
  color: color-mix(in srgb, #25d366 65%, var(--p-text-color));
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.transizione-popup-content :deep(.popup-whatsapp-callout p:last-child) {
  margin-bottom: 0;
}

/* Hint tour */
.transizione-popup-content :deep(.popup-tour-hint) {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surf-border);
  color: var(--p-text-muted-color, var(--p-surface-500));
  font-size: 0.95rem;
}

/* --- Box social + app --- */
.transizione-social {
  margin: 0.5rem 2rem 0;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  background: var(--surf-muted);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}
.transizione-fb {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  font-weight: 600;
  color: #1877f2;
  text-decoration: none;
}
.transizione-fb:hover { text-decoration: underline; }
.transizione-fb .pi { font-size: 1.15rem; }
.transizione-app {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.transizione-app-label {
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
}
.transizione-stores {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}
.transizione-stores .store-google { height: 40px; width: auto; display: block; }
.transizione-stores .store-apple { height: 34px; width: auto; display: block; }

/* --- Footer --- */
.transizione-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 2rem 1.5rem;
}
</style>
